/**
 * EstadisticasMuestras — Panel de métricas operativas de toma de muestras.
 *
 * Usa DateRangeFilter (igual que Órdenes) para filtrar por fechas.
 * Métricas: resumen, por estado, por flebotomista, distribución por hora.
 *
 * Requirements: M4.10
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Clock, Users, CheckCircle, XCircle, Activity, CalendarDays, Timer } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { DateRangeFilter } from "@/components/ui/date-range-filter";

import { useSucursal } from "@/contexts/sucursal-context";
import { useEstadisticasMuestras } from "@/hooks/use-muestras";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function hoy(): string {
  return new Date().toISOString().split("T")[0];
}

function formatMinutos(min: number | null): string {
  if (min === null || min === undefined) return "—";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${h}h ${m}m`;
}

// ─── Componente ──────────────────────────────────────────────────────────────

export function EstadisticasMuestras() {
  const { t } = useTranslation();
  const { sucursalActiva, isAllSucursalesSelected } = useSucursal();

  // Default: hoy
  const [fechaDesde, setFechaDesde] = useState(hoy());
  const [fechaHasta, setFechaHasta] = useState(hoy());

  const sucursalId = !isAllSucursalesSelected && sucursalActiva ? sucursalActiva.id : undefined;

  const params = {
    sucursal: sucursalId,
    fecha_inicio: fechaDesde || hoy(),
    fecha_fin: fechaHasta || hoy(),
  };

  const { data, isLoading } = useEstadisticasMuestras(params);

  const HORA_COLOR = "#6366f1";

  return (
    <div className="space-y-5">
      {/* ── Filtro de fechas — igual que Órdenes ────────────────────────── */}
      <div className="flex items-center gap-2">
        <DateRangeFilter
          desde={fechaDesde}
          hasta={fechaHasta}
          onChange={(desde, hasta) => {
            setFechaDesde(desde);
            setFechaHasta(hasta);
          }}
          className="w-[220px]"
        />
      </div>

      {isLoading ? (
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-28 w-full rounded-lg" />
          ))}
        </div>
      ) : !data ? null : (
        <>
          {/* ── Cards de resumen ──────────────────────────────────────────── */}
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-1 pt-4 px-4">
                <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Activity className="h-3.5 w-3.5" />
                  {t("muestras.estadisticas.total_atenciones")}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <p className="text-3xl font-bold">{data.resumen.total}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-1 pt-4 px-4">
                <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                  {t("muestras.estadisticas.cerradas")}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <p className="text-3xl font-bold text-green-600">{data.resumen.cerradas}</p>
                {data.resumen.total > 0 && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {Math.round((data.resumen.cerradas / data.resumen.total) * 100)}%
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-1 pt-4 px-4">
                <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Timer className="h-3.5 w-3.5 text-blue-500" />
                  {t("muestras.estadisticas.tiempo_atencion")}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <p className="text-3xl font-bold text-blue-600">
                  {formatMinutos(data.resumen.tiempo_atencion_promedio_min)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("muestras.estadisticas.promedio")}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-1 pt-4 px-4">
                <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-orange-500" />
                  {t("muestras.estadisticas.tiempo_cola")}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <p className="text-3xl font-bold text-orange-600">
                  {formatMinutos(data.resumen.tiempo_cola_promedio_min)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("muestras.estadisticas.promedio")}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* ── Fila inferior: flebotomistas + gráfico por hora ───────────── */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  {t("muestras.estadisticas.por_flebotomista")}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {data.por_flebotomista.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    {t("muestras.estadisticas.sin_datos")}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {data.por_flebotomista.map((f) => (
                      <div
                        key={f.flebotomista_id}
                        className="flex items-center justify-between py-1.5 border-b last:border-0"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <span className="text-xs font-semibold text-primary">
                              {f.flebotomista_nombre.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-sm font-medium truncate">
                            {f.flebotomista_nombre}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          <Badge variant="secondary" className="text-xs">
                            {f.total} {t("muestras.estadisticas.atenciones")}
                          </Badge>
                          {f.canceladas > 0 && (
                            <Badge variant="destructive" className="text-xs">
                              <XCircle className="h-3 w-3 mr-0.5" />
                              {f.canceladas}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-primary" />
                  {t("muestras.estadisticas.por_hora")}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-2 pb-4">
                {data.por_hora.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    {t("muestras.estadisticas.sin_datos")}
                  </p>
                ) : (
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart
                      data={data.por_hora}
                      margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
                    >
                      <XAxis
                        dataKey="hora"
                        tickFormatter={(h) => `${h}h`}
                        tick={{ fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        formatter={(v) => [v, t("muestras.estadisticas.atenciones")]}
                        labelFormatter={(h) => `${h}:00 — ${h}:59`}
                        contentStyle={{ fontSize: 12 }}
                      />
                      <Bar dataKey="total" radius={[3, 3, 0, 0]}>
                        {data.por_hora.map((_, i) => (
                          <Cell key={i} fill={HORA_COLOR} fillOpacity={0.85} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
